import { getPdfDelivery } from '@/lib/export-delivery'

/** Wird ausgelöst, wenn der Popup-Blocker den PDF-Tab verhindert hat. */
export const POPUP_BLOCKED_EVENT = 'symphenia:popup-blocked'

/** Wie die Datei tatsächlich ausgeliefert wurde. */
export type DeliveryResult = 'tab' | 'download' | 'download-nach-blockade'

/**
 * Hilfsfunktion zum Ausliefern eines ArrayBuffer als Datei.
 * Wird vom ExportButton und von den Monatsbericht-Seiten verwendet.
 *
 * Ob ein PDF heruntergeladen oder im Browser geöffnet wird, legt der Mandant in
 * „Branding & Layout" fest (`exportPdfDelivery`). XLSX und DOCX werden immer
 * heruntergeladen.
 *
 * @param data       ArrayBuffer aus einer Axios-Antwort (responseType: 'arraybuffer')
 * @param filename   Dateiname inkl. Erweiterung (kommt vom Content-Disposition-Header)
 * @param mimeType   MIME-Typ (kommt vom Content-Type-Header)
 * @returns          Wo die Datei gelandet ist.
 */
export function downloadFile(data: ArrayBuffer, filename: string, mimeType: string): DeliveryResult {
  const blob = new Blob([data], { type: mimeType })
  const url = URL.createObjectURL(blob)

  let blockiert = false

  if (mimeType === 'application/pdf' && getPdfDelivery() === 'tab') {
    // Der Aufruf steht hinter einem `await` auf die Export-Antwort und gilt dem
    // Browser deshalb nicht mehr als unmittelbare Benutzeraktion — Popup-Blocker
    // unterbinden ihn regelmäßig. Vorher passierte dann *gar nichts*: Der Export
    // lief durch, die Datei war erzeugt, und der Benutzer sah nur, dass nichts
    // geschah. `window.open` liefert in diesem Fall `null`.
    const tab = window.open(url, '_blank')
    if (tab) {
      // URL erst nach 30 s freigeben – der Tab braucht Zeit zum Laden
      setTimeout(() => URL.revokeObjectURL(url), 30_000)
      return 'tab'
    }
    blockiert = true
  }

  // Herunterladen — mit dem Dateinamen des Mandanten. Ein Blob-Tab hat keinen
  // Dateinamen; beim Speichern daraus ginge die Namenskonvention verloren.
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)

  if (blockiert) {
    // Die Datei ist da, aber nicht dort, wo der Mandant sie erwartet hat. Der
    // Hinweis samt Anleitung kommt aus einer Stelle für alle Aufrufer
    // (`PopupBlockedDialog` im App-Layout) — sonst müsste ihn jede Seite
    // einzeln bauen, und die Monatsbericht-Seiten hätten ihn nie bekommen.
    window.dispatchEvent(new CustomEvent(POPUP_BLOCKED_EVENT, { detail: { filename } }))
    return 'download-nach-blockade'
  }

  return 'download'
}

/**
 * Liest den Dateinamen aus dem Content-Disposition-Header.
 * Fallback auf den übergebenen Standardnamen.
 */
export function extractFilename(contentDisposition: string | undefined, fallback: string): string {
  if (!contentDisposition) return fallback
  const match = contentDisposition.match(/filename="?([^";\n]+)"?/i)
  return match?.[1] ?? fallback
}

/** MIME-Typen für Export-Formate */
export const EXPORT_MIME_TYPES = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pdf:  'application/pdf',
} as const

export type ExportFormat = keyof typeof EXPORT_MIME_TYPES
